import { Button, ButtonMode, Icon, ICONS } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useState } from 'react';
import {
  ColumnConfiguration,
  GroupedTableRowRendererProps,
  RowProps,
  TableConfiguration,
} from './GroupedTableRow.types';
import styles from './GroupedTableRow.module.scss';

interface GroupedTableRowProps<T> {
  documents: T[][];
  tableConfiguration: TableConfiguration<T>;
  groupIndex: number;
}

const cx = classNames.bind(styles);

const getRowIdentifier = (groupIndex: number, rowIndex: number, itemIndex: number) =>
  `${groupIndex}-${rowIndex}-${itemIndex}`;

const GroupedTableRow = <T extends Object>({
  documents,
  tableConfiguration,
  groupIndex,
}: GroupedTableRowProps<T>): React.ReactElement => {
  const [isGroupExpanded, setIsGroupExpanded] = useState(false);
  const [hoveredRowIdentifier, setHoveredRowIdentifier] = useState<string | null>(null);
  const { disabledItemElement: DisabledItemElement } = tableConfiguration;

  const onGroupClick = () => {
    setIsGroupExpanded((prevState) => !prevState);
  };

  const shouldRenderCell = (row: RowProps, columnIndex: number) =>
    !row.isDisabled || !row.isHovered || columnIndex < tableConfiguration.columns.length - 1 || !DisabledItemElement;

  const renderColumns = (
    group: T[],
    isFirstGroup: boolean,
    isFirstRow: boolean,
    rowIdentifier: string,
    document: T
  ) => {
    return tableConfiguration.columns.map((column, columnIndex) => {
      const shouldDisplayGroupExpandBtn = isFirstRow && columnIndex === 0 && (group.length > 1 || documents.length > 1);
      const shouldRenderEmptyCell = isFirstRow && columnIndex === 0 && group.length <= 1 && documents.length <= 1;
      const row: RowProps = {
        isDisabled: tableConfiguration.getIsDisabled?.(document),
        isHovered: hoveredRowIdentifier === rowIdentifier,
      };
      return (
        <React.Fragment key={`${column.dataKey}${tableConfiguration.getRowKey(document)}`}>
          {shouldDisplayGroupExpandBtn && (
            <td>
              <Button
                ariaLabel="expand group"
                className={cx('caret-button', {
                  'is-expanded': isGroupExpanded,
                  'gray-out': row.isHovered && row.isDisabled,
                })}
                mode={ButtonMode.TERTIARY}
                onClick={onGroupClick}
                hasEqualSidesPadding
              >
                <Icon name={ICONS.CaretRight} />
              </Button>
            </td>
          )}
          {shouldRenderEmptyCell && <td />}
          {renderCell(row, column, document, columnIndex, isFirstRow && isFirstGroup)}
        </React.Fragment>
      );
    });
  };

  const renderCell = (row: RowProps, column: ColumnConfiguration<T>, data: T, columnIndex: number, show: boolean) => {
    const { cellRenderer: CellRenderer, cellLinkRenderer: CellLinkRenderer } = column;
    const rendererProps: GroupedTableRowRendererProps<T> = {
      data,
      row,
      columnConfig: column,
      tableConfig: tableConfiguration,
      show,
    };
    return (
      <td key={`${column.dataKey}-cell`}>
        {shouldRenderCell(row, columnIndex) || !DisabledItemElement ? (
          <>
            {CellLinkRenderer && <CellLinkRenderer {...rendererProps} />}
            <CellRenderer key={column.dataKey} {...rendererProps} />
          </>
        ) : (
          <DisabledItemElement
            key={`${column.dataKey}-disabled`}
            data={data}
            row={row}
            columnConfig={column}
            tableConfig={tableConfiguration}
          />
        )}
      </td>
    );
  };

  const renderSubGroupRow = (group: T[], groupIndex: number, rowIndex: number) => {
    return group.map((data, i) => {
      const isFirstRow = i === 0;
      const rowIdentifier = getRowIdentifier(groupIndex, rowIndex, i);
      const rowKey = `${tableConfiguration.getRowKey(group[0])}-sub-row-${i}`;
      return (
        <tr
          className={cx('row', { 'is-expanded': isGroupExpanded })}
          key={rowKey}
          data-testid="subgroup-table-row"
          onMouseEnter={() => setHoveredRowIdentifier(rowIdentifier)}
          onMouseLeave={() => setHoveredRowIdentifier(null)}
        >
          <td />
          {tableConfiguration.columns.map((column, columnIndex) => {
            const row: RowProps = {
              isDisabled: tableConfiguration.getIsDisabled?.(data),
              isHovered: hoveredRowIdentifier === rowIdentifier,
            };
            return renderCell(row, column, data, columnIndex, isFirstRow && !column.cellRendererProps?.hideInSubgroup);
          })}
        </tr>
      );
    });
  };

  const renderRows = (group: T[], isFirstGroup: boolean, groupIndex: number, rowIndex: number) => {
    return group.map((data, i) => {
      const isFirstRow = i === 0;

      if (!isGroupExpanded && !isFirstRow) {
        return null;
      }
      const rowIdentifier = getRowIdentifier(groupIndex, rowIndex, i);
      return (
        <tr
          className={cx('row', { 'is-expanded': isGroupExpanded })}
          key={tableConfiguration.getRowKey(data)}
          data-testid="group-table-row"
          onMouseEnter={() => setHoveredRowIdentifier(rowIdentifier)}
          onMouseLeave={() => setHoveredRowIdentifier(null)}
        >
          {!isFirstRow && <td />}
          {renderColumns(group, isFirstGroup, isFirstRow, rowIdentifier, data)}
        </tr>
      );
    });
  };

  return (
    <tbody className={cx('body')}>
      {documents.map((group, i) => {
        const isFirstGroup = i === 0;
        if (!isFirstGroup && !isGroupExpanded) {
          return null;
        }

        return isFirstGroup ? renderRows(group, isFirstGroup, groupIndex, i) : renderSubGroupRow(group, groupIndex, i);
      })}
    </tbody>
  );
};

export default GroupedTableRow;
